/**
 * Sentry initialization for the browser bundle. Imported as the very first line
 * of `main.tsx` so the SDK's global `error` / `unhandledrejection` handlers are
 * installed before any app code can throw.
 *
 * Without `VITE_SENTRY_DSN` the SDK is never initialized and every `Sentry.*`
 * call elsewhere is a no-op, so local dev behaves exactly as before.
 */
import * as Sentry from '@sentry/react'
import { dropBrowserExtensionNoise } from './browser-extension-noise'

/** Non-negative sample rate from a Vite env var, or `fallback` if unset/invalid. */
function sampleRate(raw: string | undefined, fallback: number): number {
  const trimmed = raw?.trim()
  if (!trimmed) return fallback
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return fallback
  return parsed
}

const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()

if (dsn) {
  Sentry.init({
    dsn,
    // A production *build* is made for staging too, so MODE cannot tell them
    // apart — the deploy must pass VITE_SENTRY_ENVIRONMENT (it is inlined at
    // build time, like VITE_API_URL). MODE is only the local-dev fallback.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE,
    // Same SHA this bundle reports to the update banner, so a web issue and a
    // deploy line up. `vite.config.ts` bakes it in via `define`.
    release: __APP_VERSION_SHA__,
    dataCollection: {
      // Request bodies would include the login and change-password payloads
      // this app POSTs, so they must never be attached to an event. Matches the
      // API's instrument.ts.
      httpBodies: [],
    },
    // Errors first; raise deliberately if page/route performance is wanted.
    tracesSampleRate: sampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0),
    // An extension's own rejection has no frame of ours. denyUrls would also
    // drop one that does — see browser-extension-noise.ts.
    beforeSend: dropBrowserExtensionNoise,
  })
}
